import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import api from '../utils/api';

// ============================================
// REACT QUERY HOOKS FOR API CALLS
// ============================================

// Query Keys
export const QUERY_KEYS = {
  PROPERTIES: ['properties'],
  CLIENTS: ['clients'],
  AGENTS: ['agents'],
  PROJECTS: ['projects'],
  ACTIVITIES: ['activities'],
  DASHBOARD_STATS: ['dashboard', 'stats'],
} as const;

// ============================================
// PROPERTIES QUERIES
// ============================================

export const useProperties = (): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: QUERY_KEYS.PROPERTIES,
    queryFn: async () => {
      const response = await api.get('/properties');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useProperty = (id: string): UseQueryResult<any, Error> => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PROPERTIES, id],
    queryFn: async () => {
      const response = await api.get(`/properties/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

// ============================================
// MUTATION HOOKS WITH OPTIMISTIC UPDATES
// ============================================

export const useCreateProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (propertyData: any) => {
      const response = await api.post('/properties', propertyData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROPERTIES });
    },
  });
};

export const useUpdateProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/properties/${id}`, data);
      return response.data;
    },
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.PROPERTIES });

      // Snapshot previous value
      const previousProperties = queryClient.getQueryData(QUERY_KEYS.PROPERTIES);

      // Optimistically update
      queryClient.setQueryData(QUERY_KEYS.PROPERTIES, (old: any[] | undefined) => {
        if (!old) return old;
        return old.map(property =>
          property.id === id ? { ...property, ...data } : property
        );
      });

      return { previousProperties };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousProperties) {
        queryClient.setQueryData(QUERY_KEYS.PROPERTIES, context.previousProperties);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROPERTIES });
    },
  });
};

export const useDeleteProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/properties/${id}`);
      return id;
    },
    onMutate: async (id: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.PROPERTIES });

      // Snapshot previous value
      const previousProperties = queryClient.getQueryData(QUERY_KEYS.PROPERTIES);

      // Optimistically remove
      queryClient.setQueryData(QUERY_KEYS.PROPERTIES, (old: any[] | undefined) => {
        if (!old) return old;
        return old.filter(property => property.id !== id);
      });

      return { previousProperties };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousProperties) {
        queryClient.setQueryData(QUERY_KEYS.PROPERTIES, context.previousProperties);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROPERTIES });
    },
  });
};

export const useApproveProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.patch(`/properties/${id}/approve`);
      return response.data;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.PROPERTIES });
      const previousProperties = queryClient.getQueryData(QUERY_KEYS.PROPERTIES);

      queryClient.setQueryData(QUERY_KEYS.PROPERTIES, (old: any[] | undefined) => {
        if (!old) return old;
        return old.map(property =>
          property.id === id ? { ...property, status: 'APPROVED' } : property
        );
      });

      return { previousProperties };
    },
    onError: (_err, _id, context) => {
      if (context?.previousProperties) {
        queryClient.setQueryData(QUERY_KEYS.PROPERTIES, context.previousProperties);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROPERTIES });
    },
  });
};

export const useRejectProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, rejectionReason }: { id: string; rejectionReason: string }) => {
      const response = await api.patch(`/properties/${id}/reject`, {
        rejectionReason,
      });
      return response.data;
    },
    onMutate: async ({ id, rejectionReason }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.PROPERTIES });
      const previousProperties = queryClient.getQueryData(QUERY_KEYS.PROPERTIES);

      queryClient.setQueryData(QUERY_KEYS.PROPERTIES, (old: any[] | undefined) => {
        if (!old) return old;
        return old.map(property =>
          property.id === id ? { ...property, status: 'REJECTED', rejectionReason } : property
        );
      });

      return { previousProperties };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousProperties) {
        queryClient.setQueryData(QUERY_KEYS.PROPERTIES, context.previousProperties);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROPERTIES });
    },
  });
};

// ============================================
// PROJECTS QUERIES
// ============================================

export const useProjects = (): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECTS,
    queryFn: async () => {
      const response = await api.get('/projects');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useProjectProperties = (projectId: string): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PROJECTS, projectId, 'properties'],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/properties`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useProjectFloors = (projectId: string): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PROJECTS, projectId, 'floors'],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/floors`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// ============================================
// CLIENTS QUERIES
// ============================================

export const useClients = (): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: QUERY_KEYS.CLIENTS,
    queryFn: async () => {
      const response = await api.get('/clients');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// ============================================
// AGENTS QUERIES
// ============================================

export const useAgents = (): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: QUERY_KEYS.AGENTS,
    queryFn: async () => {
      const response = await api.get('/agents');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Keep old exports for gradual migration
export const CACHE_KEYS = {
  PROPERTIES: 'api:properties',
  CLIENTS: 'api:clients',
  AGENTS: 'api:agents',
  PROJECTS: 'api:projects',
  ACTIVITIES: 'api:activities',
  DASHBOARD_STATS: 'api:dashboard_stats',
} as const;

export const invalidateCache = (_key: string): void => {
  console.warn('[DEPRECATED] invalidateCache is deprecated. Use React Query invalidation instead.');
};

export const updateCache = <T>(
  _key: string,
  _updater: (currentData: T | null) => T | null,
  _onRollback?: (previousData: T | null) => void
): { rollback: () => void } => {
  console.warn('[DEPRECATED] updateCache is deprecated. Use React Query mutations instead.');
  return { rollback: () => {} };
};

export const invalidateCacheKeys = (_keys: string[]): void => {
  console.warn('[DEPRECATED] invalidateCacheKeys is deprecated. Use React Query invalidation instead.');
};

export const clearAllCache = (): void => {
  console.warn('[DEPRECATED] clearAllCache is deprecated. Use React Query client.clear() instead.');
};

export const getCacheStats = () => {
  console.warn('[DEPRECATED] getCacheStats is deprecated.');
  return {};
};

export const useApiCache = (
  _url: string,
  _options: any
) => {
  console.warn('[DEPRECATED] useApiCache is deprecated. Use useQuery directly instead.');
  return {
    data: null,
    loading: false,
    error: null,
    refetch: () => {},
  };
};

export const useMutationHook: any = () => {
  console.warn('[DEPRECATED] useMutation is deprecated. Use React Query useMutation instead.');
  return {};
};
